import {SC_ConfirmationLevelEnum} from "./enums/SC_ConfirmationLevel";
import {SC_WorkorderConfirmationNote} from "./SC_WorkorderConfirmationNote";
import {SC_User} from "./SC_User";
import {SC_WorkorderObject} from "./SC_WorkorderObject";
import {uniqueByKey} from "../GenericUtils";
import CrewPreviewModal from "../../components/modals/previews/CrewPreviewModal";
import EditableCardEntryPhone from "../../components/editable_entries/EditableCardEntryPhone";
import EditableCardEntryEmail from "../../components/editable_entries/EditableCardEntryEmail";
import SimpleEvenFlex from "../../components/cards/SimpleEvenFlex";

export class SC_GenericConfirmation {
    static inputKeys = {
        confirmText: 'confirm_text',
        confirmLevel: 'confirm_level',
        confirmNotes: 'confirm_notes',
        pendingContact: 'pending_contact',
        pendingNote: 'pending_note',
        confirmationArray: 'conf_array',
    } as const;

    confirmText: string | null;
    confirmLevel: SC_ConfirmationLevelEnum | null;
    workorderConfirmationNotes: SC_WorkorderConfirmationNote[] | null;
    pendingContact: string | null;
    pendingNote: string | null;

    constructor({
        confirmText = null,
        confirmLevel = null,
        workorderConfirmationNotes = null,
        pendingContact = null,
        pendingNote = null,
    } = {}) {
        this.confirmText = confirmText;
        this.confirmLevel = confirmLevel;
        this.workorderConfirmationNotes = workorderConfirmationNotes;
        this.pendingContact = pendingContact;
        this.pendingNote = pendingNote;
    }

    static fromAPI(jsonin: Record<string, any>, usersCache?: SC_User[]): SC_GenericConfirmation {
        return new SC_GenericConfirmation({
            confirmText: jsonin[SC_GenericConfirmation.inputKeys.confirmText] ?? null,
            confirmLevel: SC_ConfirmationLevelEnum.fromAPI(jsonin[SC_GenericConfirmation.inputKeys.confirmLevel]),
            workorderConfirmationNotes: jsonin[SC_GenericConfirmation.inputKeys.confirmNotes] ?
                (jsonin[SC_GenericConfirmation.inputKeys.confirmNotes] as Record<string, any>[]).map(n => SC_WorkorderConfirmationNote.fromAPI(n, usersCache)) : null,
        });
    }

    scavengeUsers(users: SC_User[]): void {
        if (this.workorderConfirmationNotes) {
            this.workorderConfirmationNotes.forEach(note => note.scavengeUsers(users));
        }
    }

    toAPI(): Record<string, unknown> {
        return {
            [SC_GenericConfirmation.inputKeys.confirmText]: this.confirmText,
            [SC_GenericConfirmation.inputKeys.confirmLevel]: this.confirmLevel?.toAPI(),
            [SC_GenericConfirmation.inputKeys.confirmNotes]: this.workorderConfirmationNotes?.map(note => note.toAPI()) ?? null,
            [SC_GenericConfirmation.inputKeys.pendingContact]: this.pendingContact,
            [SC_GenericConfirmation.inputKeys.pendingNote]: this.pendingNote,
        };
    }

    clone(): SC_GenericConfirmation {
        return new SC_GenericConfirmation({
            confirmText: this.confirmText,
            confirmLevel: this.confirmLevel.clone(),
            workorderConfirmationNotes: this.workorderConfirmationNotes ? this.workorderConfirmationNotes.map(note => note.clone()) : null,
            pendingContact: this.pendingContact,
            pendingNote: this.pendingNote,
        });
    }

    merge(other: SC_GenericConfirmation): SC_GenericConfirmation {
        if (other.confirmText !== null) this.confirmText = other.confirmText;
        if (other.confirmLevel !== null) this.confirmLevel = other.confirmLevel;
        if (other.pendingContact !== null) this.pendingContact = other.pendingContact;
        if (other.pendingNote !== null) this.pendingNote = other.pendingNote;
        if (other.workorderConfirmationNotes !== null) {
            if (!this.workorderConfirmationNotes) {
                this.workorderConfirmationNotes = [];
            }
            other.workorderConfirmationNotes.forEach(note => {
                this.workorderConfirmationNotes.push(note.clone());
            });
        }

        return this;
    }

    get isSet(): boolean {
        return this.confirmLevel !== null && this.confirmLevel.isSet();
    }

    setLevel(level: SC_ConfirmationLevelEnum): SC_GenericConfirmation {
        const out = this.clone();

        out.confirmLevel = level;

        return out;
    }

    setConfirm(label: string): SC_GenericConfirmation {
        const out = this.clone();

        out.confirmText = label;

        return out;
    }

    equals(other: SC_GenericConfirmation): boolean {
        return this.confirmText === other.confirmText &&
            this.confirmLevel.is(other.confirmLevel.toAPI());
    }

    get primaryConfirmation() : SC_WorkorderConfirmationNote | null {
        if (!this.workorderConfirmationNotes?.length)
            return null;

        const sortedNotes = this.workorderConfirmationNotes.sort((a, b) => b.confirmBackingNote.noteCreatedTime.getTime() - a.confirmBackingNote.noteCreatedTime.getTime())

        const firstIndex = sortedNotes.findIndex(it => it.isBackwards);

        const slicedNotes = sortedNotes.slice(0, firstIndex >= 0 ? firstIndex : undefined);

        if (slicedNotes.length > 0) {
            if (slicedNotes.length > 1)
                return slicedNotes[1];
            else if (!this.confirmLevel.is(SC_ConfirmationLevelEnum.FINAL))
                return slicedNotes[0];
            else return null;
        } else {
            return null;
        }
    }

    get secondaryConfirmation() : SC_WorkorderConfirmationNote | null {
        if (!this.workorderConfirmationNotes?.length)
            return null;

        const sortedNotes = this.workorderConfirmationNotes.sort((a, b) => b.confirmBackingNote.noteCreatedTime.getTime() - a.confirmBackingNote.noteCreatedTime.getTime())

        const firstIndex = sortedNotes.findIndex(it => it.isBackwards);

        const slicedNotes = sortedNotes.slice(0, firstIndex >= 0 ? firstIndex : undefined);

        if (slicedNotes.length > 0) {
            if (this.confirmLevel.is(SC_ConfirmationLevelEnum.FINAL))
                return slicedNotes[0];
            else if (slicedNotes.length > 1)
                return slicedNotes[0];
            else return null;
        } else {
            return null;
        }
    }

    specialInline(workorder: SC_WorkorderObject) {
        switch (this.confirmText?.toLowerCase()) {
            case "crew":
                return uniqueByKey((workorder?.services.map(it => it.crew) ?? []).filter(Boolean), "internalID").map(crew => (
                    <CrewPreviewModal key={crew.internalID} crew={crew}/>
                ));
            case "client":
                return <SimpleEvenFlex enforceEven={false} invertDirections={true}>
                    <span>{workorder.location.preferred_contact_name}</span>
                    <span><EditableCardEntryPhone baseValue={workorder.location.preferred_contact_phone} isEditing={false} hyperlinkDisplay={true}/></span>
                    <span><EditableCardEntryEmail hyperlinkDisplay={true} isEditing={false} baseValue={workorder.location.preferred_contact_email}/></span>
                </SimpleEvenFlex>
        }
    }

    defaultConfirmText(workorder: SC_WorkorderObject): string {
        switch (this.confirmText?.toLowerCase()) {
            case "crew":
                return uniqueByKey((workorder?.services.map(it => it.crew) ?? []).filter(Boolean), "internalID").map(crew => (
                    crew.lead?.displayName
                )).filter(Boolean).slice(0, 1).at(0);
            case "client":
                return workorder.location.preferred_contact_name;
        }
    }
}
